import {
  COMPLETED_OPTIONS,
  DUE_OPTIONS,
  type Filters,
  type TaskView,
} from './task-utils';

/** The views, as the address names them. */
export const TASK_VIEW_KEYS: readonly TaskView[] = [
  'mine',
  'calls',
  'replies',
  'review',
  'team',
  'completed',
];

/** What the address holds of the page: the view and the filters. */
export type TasksUrlState = {
  view?: TaskView;
  q?: string;
  due?: string;
  sequence?: string;
  owner?: string;
  priority?: string;
  type?: string;
  state?: string;
  outcome?: string;
  completed?: string;
};

export const TASKS_URL_KEYS = [
  'view',
  'q',
  'due',
  'sequence',
  'owner',
  'priority',
  'type',
  'state',
  'outcome',
  'completed',
] as const satisfies readonly (keyof TasksUrlState)[];

const DEFAULTS = {
  view: 'mine',
  due: 'due_now',
  sequence: 'all',
  priority: 'all',
  type: 'all',
  state: 'all',
  outcome: 'all',
  completed: 'any',
} as const;

/**
 * The filters as search params: only what differs from where the page
 * starts, so the plain address stays plain.
 */
export function encodeTaskFilters(filters: Filters): TasksUrlState {
  const keep = <K extends keyof typeof DEFAULTS>(
    key: K,
    value: string
  ): string | undefined => (value === DEFAULTS[key] ? undefined : value);
  return {
    view: keep('view', filters.view) as TaskView | undefined,
    q: filters.query.trim() || undefined,
    due: keep('due', filters.due),
    sequence: keep('sequence', filters.sequence),
    owner: filters.owner ?? undefined,
    priority: keep('priority', filters.priority),
    type: keep('type', filters.type),
    state: keep('state', filters.state),
    outcome: keep('outcome', filters.outcome),
    completed: keep('completed', filters.completed),
  };
}

const oneOf = <T extends string>(
  value: string | undefined,
  allowed: readonly T[]
): T | undefined =>
  value !== undefined && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined;

/** The filters an address names; anything it gets wrong is left at its default. */
export function decodeTaskFilters(search: TasksUrlState): Partial<Filters> {
  const out: Partial<Filters> = {};
  const view = oneOf(search.view, TASK_VIEW_KEYS);
  if (view) out.view = view;
  if (search.q) out.query = search.q;
  const due = oneOf(
    search.due,
    DUE_OPTIONS.map(([key]) => key)
  );
  if (due) out.due = due;
  if (search.sequence) out.sequence = search.sequence;
  if (search.owner) out.owner = search.owner;
  const priority = oneOf(search.priority, ['High', 'Medium', 'Low'] as const);
  if (priority) out.priority = priority;
  const type = oneOf(search.type, ['Call', 'Manual', 'Reply'] as const);
  if (type) out.type = type;
  const state = oneOf(search.state, ['ready', 'blocked'] as const);
  if (state) out.state = state;
  if (search.outcome) out.outcome = search.outcome;
  const completed = oneOf(
    search.completed,
    COMPLETED_OPTIONS.map(([key]) => key)
  );
  if (completed) out.completed = completed;
  return out;
}
